import type { Project, CalculationResults, MonthlyCashFlow } from './api';

// Mock project data
export const mockProject: Project = {
  id: 'mock-project-1',
  name: 'Riverside Real Estate Development',
  address: '1234 Main Street',
  city: 'Downtown',
  state: 'CA',
  zip_code: '90210',
  retail_type: 'Community Center',
  total_nra: 125000,
  analysis_start_date: '2025-01-15',
  created_at: '2025-01-15T10:00:00Z',
  updated_at: '2025-10-26T10:00:00Z',
};

// Helper to generate monthly cash flows
const generateMonthlyCashFlows = (): MonthlyCashFlow[] => {
  const flows: MonthlyCashFlow[] = [];
  let cumulativeEquity = 0;
  let cumulativeDebt = 0;
  let loanBalance = 0;

  for (let month = 1; month <= 60; month++) {
    const isConstruction = month <= 18;
    const isStabilizing = month > 18 && month <= 30;
    const isStabilized = month > 30;

    // Construction costs (development phase)
    const landCosts = month === 1 ? 8500000 : 0;
    const hardCosts = isConstruction ? 1597222 : 0; // $28.75M over 18 months
    const softCosts = isConstruction ? 291667 : 0; // $5.25M over 18 months
    const developmentCosts = landCosts + hardCosts + softCosts;

    // Revenue (ramps up after construction)
    const baseRent = isStabilized ? 285000 : (isStabilizing ? (month - 18) * 23750 : 0);
    const recoveries = baseRent * 0.15;
    const totalRevenue = baseRent + recoveries;
    const vacancy = totalRevenue * 0.05;
    const effectiveRevenue = totalRevenue - vacancy;

    // Expenses (operations)
    const propertyTaxes = isStabilized ? 35000 : (isStabilizing ? (month - 18) * 2916 : 0);
    const insurance = isStabilized ? 8500 : (isStabilizing ? (month - 18) * 708 : 0);
    const utilities = isStabilized ? 12000 : (isStabilizing ? (month - 18) * 1000 : 0);
    const maintenance = isStabilized ? 15000 : (isStabilizing ? (month - 18) * 1250 : 0);
    const management = effectiveRevenue * 0.03;
    const marketing = isStabilized ? 5000 : (isStabilizing ? 2000 : 0);
    const other = isStabilized ? 8500 : (isStabilizing ? 1500 : 0);
    const totalExpenses = propertyTaxes + insurance + utilities + maintenance + management + marketing + other;

    const noi = effectiveRevenue - totalExpenses;

    // Debt (60% LTC)
    const debtDraws = isConstruction ? developmentCosts * 0.6 : 0;
    const interestRate = 0.065 / 12; // 6.5% annual
    loanBalance += debtDraws;
    const interestExpense = loanBalance * interestRate;
    const principalPayment = isStabilized ? 85000 : 0;
    loanBalance -= principalPayment;
    const debtService = interestExpense + principalPayment;

    const cashFlowBeforeFinancing = noi - developmentCosts;
    const cashFlowAfterFinancing = cashFlowBeforeFinancing + debtDraws - debtService;
    const equityCashFlow = developmentCosts - debtDraws + (noi - debtService);

    cumulativeEquity += (developmentCosts - debtDraws);
    cumulativeDebt += debtDraws;

    flows.push({
      month,
      date: new Date(2025, month - 1, 1).toISOString().split('T')[0],
      period: `Month ${month}`,
      landCosts,
      hardCosts,
      softCosts,
      developmentCosts,
      revenue: {
        month,
        baseRent,
        recoveries,
        otherIncome: 0,
        totalRevenue,
        vacancy,
        effectiveRevenue,
      },
      expenses: {
        month,
        propertyTaxes,
        insurance,
        utilities,
        maintenance,
        management,
        marketing,
        other,
        totalExpenses,
      },
      noi,
      debtDraws,
      interestExpense,
      principalPayment,
      debtService,
      loanBalance,
      cashFlowBeforeFinancing,
      cashFlowAfterFinancing,
      equityCashFlow,
      cumulativeEquity,
      cumulativeDebt,
    });
  }

  return flows;
};

// Mock calculation results
export const mockCalculationResults: CalculationResults = {
  projectId: 'mock-project-1',
  monthlyCashFlows: generateMonthlyCashFlows(),
  annualCashFlows: [
    {
      year: 1,
      revenue: 0,
      expenses: 2850000,
      noi: -2850000,
      debtService: 450000,
      cashFlow: -12650000,
    },
    {
      year: 2,
      revenue: 1450000,
      expenses: 3250000,
      noi: -1800000,
      debtService: 850000,
      cashFlow: -18950000,
    },
    {
      year: 3,
      revenue: 3420000,
      expenses: 1140000,
      noi: 2280000,
      debtService: 1140000,
      cashFlow: 1140000,
    },
    {
      year: 4,
      revenue: 3420000,
      expenses: 1140000,
      noi: 2280000,
      debtService: 1140000,
      cashFlow: 2280000,
    },
    {
      year: 5,
      revenue: 3420000,
      expenses: 1140000,
      noi: 2280000,
      debtService: 1140000,
      cashFlow: 2280000,
    },
  ],
  returnMetrics: {
    leveredIRR: 0.1842,
    unleveredIRR: 0.1256,
    equityMultiple: 2.35,
    averageCashOnCash: 0.1245,
    totalCashInvested: 18300000,
    totalCashReturned: 43005000,
  },
  reversion: {
    reversionMonth: 60,
    stabilizedNOI: 3330000,
    exitCapRate: 0.0685,
    grossSalePrice: 48613139,
    sellingCosts: 486131,
    brokerCommission: 972262,
    totalTransactionCosts: 1458393,
    netSaleProceeds: 47154746,
    loanPayoff: 24850000,
    netProceedsToEquity: 22304746,
  },
  totalProjectCost: 45750000,
  totalEquityInvested: 18300000,
  totalDebtFinancing: 27450000,
  stabilizedNOI: 3330000,
  yieldOnCost: 0.0728,
  developmentSpread: 2863139,
  noiCapAtStabilization: 0.0685,
  totalMonths: 60,
  totalYears: 5,
};

// Mock project inputs (for Project Details page)
export const mockProjectInputs = {
  // Investment Description
  propertyName: 'Riverside Real Estate Development',
  address: '1234 Main Street',
  city: 'Downtown',
  state: 'CA',
  zipCode: '90210',
  retailType: 'Community Center',
  totalNRA: 125000,
  analysisStartDate: '2025-01-15',

  // Development Timeline
  constructionStartMonth: 1,
  constructionEndMonth: 18,
  leaseUpStartMonth: 12,
  stabilizationMonth: 30,
  reversionMonth: 60,

  // Land Costs
  landAcquisition: 8500000,
  landClosingCosts: 255000,
  landTotal: 8755000,

  // Hard Costs
  hardCostsPerSF: 230,
  hardCostsTotal: 28750000,
  sCurveDistribution: 'standard',
  sCurveSteepness: 10,

  // Soft Costs
  architecturalEngineering: 1250000,
  developmentFee: 1375000,
  legalAccounting: 625000,
  permits: 450000,
  insurance: 350000,
  contingency: 1200000,
  softCostsTotal: 5250000,

  // Financing
  loanToConstruction: 0.60,
  constructionInterestRate: 0.065,
  permanentLoanAmount: 27450000,
  permanentInterestRate: 0.055,
  permanentAmortization: 30,
  loanOriginationFee: 0.01,

  // Operating Assumptions
  vacancyRate: 0.05,
  managementFeeRate: 0.03,
  propertyTaxRate: 0.0125,
  insurancePerSF: 0.85,
  maintenancePerSF: 1.50,
  utilitiesPerSF: 1.20,
  marketingBudget: 60000,

  // Reversion
  exitCapRate: 0.0685,
  sellingCosts: 0.03,
  brokerCommission: 0.02,

  // Partnership Structure
  lpPercentage: 0.90,
  gpPercentage: 0.10,
  preferredReturn: 0.08,
  gpPromoteTier1: 0.20,
  gpPromoteTier2: 0.30,
  catchup: true,
};

// Mock scenarios for comparison
export const mockScenarios = [
  {
    id: 'base-case',
    name: 'Base Case',
    description: 'Conservative assumptions',
    assumptions: { ...mockProjectInputs },
    results: { ...mockCalculationResults },
  },
  {
    id: 'optimistic',
    name: 'Optimistic',
    description: 'Best case scenario',
    assumptions: {
      ...mockProjectInputs,
      vacancyRate: 0.03,
      exitCapRate: 0.06,
    },
    results: {
      ...mockCalculationResults,
      leveredIRR: 0.2145,
      unleveredIRR: 0.1489,
      developmentSpread: 3850000,
    },
  },
  {
    id: 'pessimistic',
    name: 'Pessimistic',
    description: 'Conservative case',
    assumptions: {
      ...mockProjectInputs,
      vacancyRate: 0.08,
      exitCapRate: 0.075,
      hardCostsPerSF: 250,
    },
    results: {
      ...mockCalculationResults,
      leveredIRR: 0.1425,
      unleveredIRR: 0.0985,
      developmentSpread: 1950000,
    },
  },
];

// Mock sensitivity analysis data
export const mockSensitivityData = {
  variables: [
    {
      name: 'Exit Cap Rate',
      key: 'exitCapRate',
      baseValue: 0.0685,
      min: 0.055,
      max: 0.085,
      step: 0.005,
    },
    {
      name: 'Construction Cost ($/SF)',
      key: 'hardCostsPerSF',
      baseValue: 230,
      min: 200,
      max: 270,
      step: 10,
    },
    {
      name: 'Vacancy Rate',
      key: 'vacancyRate',
      baseValue: 0.05,
      min: 0.02,
      max: 0.10,
      step: 0.01,
    },
  ],
  results: [
    {
      variable: 'Exit Cap Rate',
      impact: -0.35, // -35% change in IRR per 1% change
      values: [
        { input: 0.055, irr: 0.2384 },
        { input: 0.060, irr: 0.2145 },
        { input: 0.0685, irr: 0.1842 },
        { input: 0.075, irr: 0.1625 },
        { input: 0.085, irr: 0.1285 },
      ],
    },
    {
      variable: 'Construction Cost ($/SF)',
      impact: -0.22,
      values: [
        { input: 200, irr: 0.2145 },
        { input: 220, irr: 0.1985 },
        { input: 230, irr: 0.1842 },
        { input: 250, irr: 0.1625 },
        { input: 270, irr: 0.1425 },
      ],
    },
    {
      variable: 'Vacancy Rate',
      impact: -0.18,
      values: [
        { input: 0.02, irr: 0.1985 },
        { input: 0.03, irr: 0.1925 },
        { input: 0.05, irr: 0.1842 },
        { input: 0.08, irr: 0.1685 },
        { input: 0.10, irr: 0.1585 },
      ],
    },
  ],
};

// Mock tenants data
export interface MockTenant {
  id: string;
  name: string;
  category: string;
  icon: string;
  nra: number;
  rentPerSF: number;
  leaseStartMonth: number;
  rentStartMonth: number;
  rentGrowthRate: number;
  tiAllowance: number;
  freeRentMonths: number;
  status: 'Leased' | 'Negotiating' | 'Prospective';
}

export const mockTenants: MockTenant[] = [
  {
    id: '1',
    name: 'Whole Foods Market',
    category: 'Grocery',
    icon: '🛒',
    nra: 45000,
    rentPerSF: 22.0,
    leaseStartMonth: 18,
    rentStartMonth: 18,
    rentGrowthRate: 0.02,
    tiAllowance: 25.0,
    freeRentMonths: 0,
    status: 'Leased',
  },
  {
    id: '2',
    name: 'Starbucks Coffee',
    category: 'Restaurant',
    icon: '☕',
    nra: 2500,
    rentPerSF: 85.0,
    leaseStartMonth: 20,
    rentStartMonth: 20,
    rentGrowthRate: 0.025,
    tiAllowance: 40.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '3',
    name: 'Chase Bank',
    category: 'Financial',
    icon: '🏦',
    nra: 4000,
    rentPerSF: 45.0,
    leaseStartMonth: 19,
    rentStartMonth: 19,
    rentGrowthRate: 0.02,
    tiAllowance: 50.0,
    freeRentMonths: 1,
    status: 'Leased',
  },
  {
    id: '4',
    name: 'LA Fitness',
    category: 'Fitness',
    icon: '💪',
    nra: 25000,
    rentPerSF: 18.0,
    leaseStartMonth: 21,
    rentStartMonth: 22,
    rentGrowthRate: 0.025,
    tiAllowance: 30.0,
    freeRentMonths: 3,
    status: 'Leased',
  },
  {
    id: '5',
    name: "Trader Joe's",
    category: 'Grocery',
    icon: '🍎',
    nra: 12000,
    rentPerSF: 28.0,
    leaseStartMonth: 20,
    rentStartMonth: 20,
    rentGrowthRate: 0.02,
    tiAllowance: 20.0,
    freeRentMonths: 0,
    status: 'Leased',
  },
  {
    id: '6',
    name: 'Panera Bread',
    category: 'Restaurant',
    icon: '🥖',
    nra: 4500,
    rentPerSF: 65.0,
    leaseStartMonth: 22,
    rentStartMonth: 23,
    rentGrowthRate: 0.025,
    tiAllowance: 45.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '7',
    name: 'CVS Pharmacy',
    category: 'Pharmacy',
    icon: '💊',
    nra: 13500,
    rentPerSF: 24.0,
    leaseStartMonth: 19,
    rentStartMonth: 19,
    rentGrowthRate: 0.02,
    tiAllowance: 20.0,
    freeRentMonths: 0,
    status: 'Leased',
  },
  {
    id: '8',
    name: 'Chipotle Mexican Grill',
    category: 'Restaurant',
    icon: '🌯',
    nra: 2800,
    rentPerSF: 95.0,
    leaseStartMonth: 21,
    rentStartMonth: 22,
    rentGrowthRate: 0.03,
    tiAllowance: 50.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '9',
    name: 'Target',
    category: 'Retail',
    icon: '🎯',
    nra: 35000,
    rentPerSF: 20.0,
    leaseStartMonth: 18,
    rentStartMonth: 18,
    rentGrowthRate: 0.02,
    tiAllowance: 15.0,
    freeRentMonths: 0,
    status: 'Leased',
  },
  {
    id: '10',
    name: 'Ulta Beauty',
    category: 'Beauty',
    icon: '💄',
    nra: 10000,
    rentPerSF: 32.0,
    leaseStartMonth: 22,
    rentStartMonth: 23,
    rentGrowthRate: 0.025,
    tiAllowance: 35.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '11',
    name: 'The Cheesecake Factory',
    category: 'Restaurant',
    icon: '🍰',
    nra: 8000,
    rentPerSF: 55.0,
    leaseStartMonth: 23,
    rentStartMonth: 24,
    rentGrowthRate: 0.025,
    tiAllowance: 60.0,
    freeRentMonths: 3,
    status: 'Negotiating',
  },
  {
    id: '12',
    name: 'Petco',
    category: 'Pet Supply',
    icon: '🐕',
    nra: 15000,
    rentPerSF: 22.0,
    leaseStartMonth: 21,
    rentStartMonth: 21,
    rentGrowthRate: 0.02,
    tiAllowance: 25.0,
    freeRentMonths: 1,
    status: 'Leased',
  },
  {
    id: '13',
    name: 'Lululemon',
    category: 'Apparel',
    icon: '👕',
    nra: 4000,
    rentPerSF: 75.0,
    leaseStartMonth: 24,
    rentStartMonth: 24,
    rentGrowthRate: 0.03,
    tiAllowance: 40.0,
    freeRentMonths: 2,
    status: 'Negotiating',
  },
  {
    id: '14',
    name: 'Five Guys',
    category: 'Restaurant',
    icon: '🍔',
    nra: 3000,
    rentPerSF: 80.0,
    leaseStartMonth: 22,
    rentStartMonth: 23,
    rentGrowthRate: 0.025,
    tiAllowance: 45.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '15',
    name: 'Barnes & Noble',
    category: 'Bookstore',
    icon: '📚',
    nra: 20000,
    rentPerSF: 18.0,
    leaseStartMonth: 20,
    rentStartMonth: 20,
    rentGrowthRate: 0.02,
    tiAllowance: 20.0,
    freeRentMonths: 1,
    status: 'Leased',
  },
  {
    id: '16',
    name: 'Orangetheory Fitness',
    category: 'Fitness',
    icon: '🏃',
    nra: 3500,
    rentPerSF: 42.0,
    leaseStartMonth: 23,
    rentStartMonth: 24,
    rentGrowthRate: 0.03,
    tiAllowance: 35.0,
    freeRentMonths: 2,
    status: 'Prospective',
  },
  {
    id: '17',
    name: 'Jersey Mikes Subs',
    category: 'Restaurant',
    icon: '🥪',
    nra: 2200,
    rentPerSF: 72.0,
    leaseStartMonth: 22,
    rentStartMonth: 23,
    rentGrowthRate: 0.025,
    tiAllowance: 40.0,
    freeRentMonths: 2,
    status: 'Leased',
  },
  {
    id: '18',
    name: 'Supercuts',
    category: 'Beauty',
    icon: '✂️',
    nra: 1800,
    rentPerSF: 38.0,
    leaseStartMonth: 21,
    rentStartMonth: 21,
    rentGrowthRate: 0.02,
    tiAllowance: 30.0,
    freeRentMonths: 1,
    status: 'Leased',
  },
  {
    id: '19',
    name: 'AT&T Store',
    category: 'Telecommunications',
    icon: '📱',
    nra: 3000,
    rentPerSF: 55.0,
    leaseStartMonth: 20,
    rentStartMonth: 20,
    rentGrowthRate: 0.025,
    tiAllowance: 45.0,
    freeRentMonths: 1,
    status: 'Leased',
  },
  {
    id: '20',
    name: 'Noodles & Company',
    category: 'Restaurant',
    icon: '🍜',
    nra: 2500,
    rentPerSF: 68.0,
    leaseStartMonth: 24,
    rentStartMonth: 25,
    rentGrowthRate: 0.025,
    tiAllowance: 40.0,
    freeRentMonths: 2,
    status: 'Prospective',
  },
];

// Helper to enable/disable mock mode
export const USE_MOCK_DATA = true; // Set to false when API is working
